import React, { useState, Fragment, useEffect } from "react";
import { Container, Form, Button, Alert, Spinner } from "react-bootstrap";
import { useQueryString } from "@/hooks";
import { usePartnerQuery } from "../../api";
import { useAuthV2 } from "@/offering/auth_portal_custom_login_provider";
import { isDuplicateSignup } from "@/offering/portal_auth_errors";
import { colors } from "@/offering_v2/components/colors";
import { Spacer } from "@/offering/spacer";
import { Text } from "@/offering/text";
// @ts-ignore
import passwordPolicy from "password-sheriff";
// @ts-ignore
import { format } from "password-sheriff/lib/helper";
import { Icon } from "@/components";
import { PartnerConfig } from "../../partner_config";
import { portalLoginHref } from "../../portal_paths";
import {
  storePartnerSlug,
  usePortalPartnerSlug,
} from "../../use_portal_partner_slug";

const pwdPolicy = passwordPolicy("good");

type SignupError = "passwordPolicy" | "userExists" | "generic";

type RequirementColor = "success" | "error" | "default";

// An unmet requirement only reads as a problem once the form has actually
// rejected the password; until then it is just a checklist item not yet ticked.
function requirementColor(args: {
  verified?: boolean;
  showProblems: boolean;
}): RequirementColor {
  if (args.verified) return "success";
  return args.showProblems ? "error" : "default";
}

interface SignupErrorAlertProps {
  error: SignupError;
  loginHref: string;
}

function SignupErrorAlert(props: SignupErrorAlertProps) {
  const { error, loginHref } = props;

  // An address that is already taken is not a fault, and dressing it as one
  // sends a returning customer hunting for a problem to fix — they retry, or
  // give up, when all they needed was the login page.
  if (error === "userExists") {
    return (
      <Alert className="mb-2" variant="warning">
        That email address already has an account.{" "}
        <Alert.Link href={loginHref}>Log in instead</Alert.Link>
      </Alert>
    );
  }

  return (
    <Alert className="mb-2" variant="danger">
      {error === "passwordPolicy"
        ? "Please fix the password requirements"
        : "An error occurred during signup"}
    </Alert>
  );
}

export function PortalSignupPage(props: { partnerConfig: PartnerConfig }) {
  const { partnerConfig } = props;
  const query = useQueryString();
  const partnerSlug = usePortalPartnerSlug();
  const auth0 = useAuthV2();
  const partner = usePartnerQuery(partnerSlug);
  const [email, setEmail] = useState(query.email);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<SignupError | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const explainedPwdPolicy = pwdPolicy.explain();
  const missingReqs = password ? pwdPolicy.missing(password) : { rules: [] };
  // The requirement list turns red only for a password rejection, never for a
  // failure that has nothing to do with the password: someone told the address
  // is already taken should not also see every password rule flagged at them.
  const showPasswordProblems = error === "passwordPolicy";
  // Carries the address actually typed rather than the one the URL arrived with:
  // on a duplicate signup the typed address is the one holding the account.
  const loginHref = portalLoginHref({ partnerSlug, email });

  // The duplicate-signup warning names an address, and the login link beside it
  // carries that same address — so both go stale the moment the field is edited,
  // and the link would then prefill the login form with an address that has no
  // account. Retiring the error on the first keystroke keeps the message, the
  // link and the field describing one address.
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setError(null);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Belt and braces. The submit button stays disabled until the policy
    // passes, so this branch and the copy it selects are unreachable as the
    // form stands; it is kept because it is the only thing that would stop a
    // policy-failing password reaching the identity provider if that gating
    // ever moves.
    if (!pwdPolicy.check(password)) {
      setError("passwordPolicy");
      return;
    }

    setIsLoading(true);

    try {
      await auth0.signup(email, password);
    } catch (err) {
      setError(isDuplicateSignup(err) ? "userExists" : "generic");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (partnerSlug) {
      storePartnerSlug(partnerSlug);
    }
  }, [partnerSlug]);

  if (!partner) {
    return null;
  }

  return (
    <Container className="mt-5">
      <div className="d-flex flex-column align-items-center">
        {(partnerConfig.logoURL || partner.logoUrl) && (
          <img
            src={partnerConfig.logoURL || partner.logoUrl}
            alt={partner.name}
            style={{ maxWidth: "120px", marginBottom: "0.5rem" }}
          />
        )}
        <div className="col-12 col-md-6 col-lg-4">
          <h2 className="text-center mb-4">Sign up for {partner.name}</h2>
          {error && <SignupErrorAlert error={error} loginHref={loginHref} />}
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={handleEmailChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={handlePasswordChange}
                required
              />
              <Spacer size={16} />
              <Text weight="bold" size="xs">
                Password must
              </Text>
              <Spacer size={8} />
              {explainedPwdPolicy.map((policy: any, index: number) => {
                const isVerified = missingReqs.rules[index]?.verified;
                const textColor = requirementColor({
                  verified: isVerified,
                  showProblems: showPasswordProblems,
                });
                const iconClass = `text-color-${textColor}`;

                return (
                  <Fragment key={policy.message}>
                    <div className="d-flex align-items-center">
                      <Icon
                        className={iconClass}
                        name={isVerified ? "check" : "x"}
                        size={16}
                        color={textColor}
                      />
                      <Spacer direction="horizontal" size={4} />
                      <Text size="xs" color={textColor}>
                        {format(policy.message, ...policy.format)}
                      </Text>
                    </div>
                    <Spacer size={4} />
                    {policy.items?.map?.((item: any, itemIndex: number) => {
                      const isItemVerified =
                        missingReqs.rules[index]?.items[itemIndex]?.verified;
                      const policyTextColor = requirementColor({
                        verified: isItemVerified,
                        showProblems: showPasswordProblems,
                      });

                      return (
                        <Fragment key={item.message}>
                          <Text
                            className="ps-4"
                            size="xs"
                            color={policyTextColor}
                          >
                            {"     "}• {item.message}
                          </Text>
                          <Spacer size={4} />
                        </Fragment>
                      );
                    })}
                  </Fragment>
                );
              })}
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              disabled={isLoading || !pwdPolicy.check(password)}
              className="w-100 d-flex align-items-center justify-content-center gap-2 mt-4"
            >
              {isLoading && <Spinner animation="border" size="sm" />}
              {isLoading ? "Creating account..." : "Sign Up"}
            </Button>

            <div className="text-center mt-3">
              <a style={{ color: colors.eggPlantCore }} href={loginHref}>
                Already have an account? Log in
              </a>
            </div>
          </Form>
        </div>
      </div>
    </Container>
  );
}
